import { motion } from "framer-motion";
import { Play, ExternalLink } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useSite } from "@/context/SiteContext";
import { SectionHead, Reveal } from "@/components/Reveal";
import { YouTubeIcon } from "@/components/icons";

const cleanTitle = (s = "") => s.replace(/#\w+/g, "").replace(/\s*\|\s*Ananawi\s*$/i, "").trim();

const VideoCard = ({ v, onPlay, tall, i }) => (
  <motion.button
    type="button"
    onClick={() => onPlay(v)}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.6, delay: (i % 6) * 0.06 }}
    whileHover={{ y: -6, rotate: i % 2 ? 1.2 : -1.2 }}
    className={`group text-left shrink-0 snap-start ${tall ? "w-[200px] sm:w-[220px]" : "w-full"}`}
    data-testid={`video-card-${v.id}`}
  >
    <div className={`relative overflow-hidden rounded-[22px] sticker bg-ink ${tall ? "aspect-[9/16]" : "aspect-video"}`}>
      <img src={v.thumbnail} alt={cleanTitle(v.title)} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center w-14 h-14 rounded-full bg-sun sticker-sm transition-transform duration-300 group-hover:scale-125">
        <Play className="w-6 h-6 fill-ink text-ink ml-0.5" />
      </span>
      <p className="absolute bottom-3 left-3 right-3 text-white font-bold text-sm leading-snug line-clamp-2">{cleanTitle(v.title)}</p>
    </div>
  </motion.button>
);

const Backdrop = ({ id, consent }) => {
  if (!id) return null;
  if (consent === "all") {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
        <iframe
          title="background"
          className="absolute top-1/2 left-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full -translate-x-1/2 -translate-y-1/2 scale-125"
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&modestbranding=1&playsinline=1&rel=0`}
          allow="autoplay; encrypted-media"
          tabIndex={-1}
        />
      </div>
    );
  }
  return <img src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 blur-sm scale-110" />;
};

export const VideoSection = () => {
  const { t } = useI18n();
  const { videos, setPlayer, consent, content } = useSite();
  const list = videos?.videos ?? [];
  const longs = list.filter((v) => !v.is_short);
  const manuallyFeatured = longs.find((v) => v.id === (videos?.manual_featured_id ?? videos?.featured_id));
  const latestVideo = [...longs].sort((a, b) => new Date(b.published || 0) - new Date(a.published || 0))[0];
  const featured = manuallyFeatured ?? latestVideo;
  const play = (v) => setPlayer({ id: v.id, title: cleanTitle(v.title), short: v.is_short });

  return (
    <section id="video" className="relative overflow-hidden bg-ink text-white py-24 sm:py-32 stars-bg" data-testid="video-section">
      <Backdrop id={featured?.id} consent={consent} />
      <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/70 to-ink pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHead eyebrow={t.video.eyebrow} title={t.video.title} sub={t.video.sub} tone="light" testid="video-section-head" eyebrowClass="bg-pink !text-white" />
          <Reveal y={20} className="flex flex-wrap gap-3">
            <a href={content?.socials?.youtube} target="_blank" rel="noopener noreferrer" className="btn-pop inline-flex items-center gap-2 rounded-full bg-[#ff0000] text-white px-5 py-2 font-display font-semibold border-[2.5px] border-white" data-testid="video-channel-link">
              <YouTubeIcon className="w-5 h-5" /> {t.video.channel} <ExternalLink className="w-4 h-4" />
            </a>
          </Reveal>
        </div>

        {longs.length === 0 && <p className="mt-12 text-white/80" data-testid="video-empty">{t.video.empty}</p>}

        {featured && (
          <Reveal className="mt-14">
            <button onClick={() => play(featured)} className="group relative block w-full overflow-hidden rounded-[32px] border-[3px] border-white shadow-[10px_10px_0_#ff4d8d] text-left" data-testid="video-featured">
              <div className="aspect-[16/9] sm:aspect-[21/9] relative">
                <img src={`https://i.ytimg.com/vi/${featured.id}/maxresdefault.jpg`} onError={(e) => { e.currentTarget.src = featured.thumbnail; }} alt={cleanTitle(featured.title)} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
                <div className="absolute left-5 sm:left-10 bottom-5 sm:bottom-10 right-5 max-w-xl">
                  <span className="inline-flex items-center gap-2 rounded-full bg-sun text-ink px-3 py-1 text-xs font-extrabold uppercase">{t.video.play}</span>
                  <p className="mt-3 font-display font-bold text-xl sm:text-3xl leading-tight">{cleanTitle(featured.title)}</p>
                </div>
                <span className="absolute right-5 sm:right-10 bottom-5 sm:bottom-10 grid place-items-center w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-pink border-[3px] border-white transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12">
                  <Play className="w-8 h-8 sm:w-10 sm:h-10 fill-white ml-1" />
                </span>
              </div>
            </button>
          </Reveal>
        )}

        {longs.length > 0 && (
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="video-grid-long">
            {longs.map((v, i) => <VideoCard key={v.id} v={v} onPlay={play} i={i} />)}
          </div>
        )}


      </div>
    </section>
  );
};
